import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ScheduledExportsPanel = ({ className = '' }) => {
  const [isCreatingSchedule, setIsCreatingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    reportType: 'best_price',
    frequency: 'weekly',
    time: '09:00',
    recipients: '',
    enabled: true
  });

  const scheduledExports = [
    {
      id: 1,
      name: "Weekly Best Price Report",
      reportType: "best_price",
      frequency: "weekly",
      nextRun: "2025-10-24 09:00",
      recipients: ["procurement@company.com", "manager@company.com"],
      enabled: true,
      lastRun: "2025-10-17 09:00",
      status: "success"
    },
    {
      id: 2,
      name: "Monthly Supplier Analysis",
      reportType: "all_groups",
      frequency: "monthly",
      nextRun: "2025-11-01 08:00",
      recipients: ["analytics@company.com"],
      enabled: true,
      lastRun: "2025-10-01 08:00",
      status: "success"
    },
    {
      id: 3,
      name: "Daily Ambiguous Matches",
      reportType: "ambiguous",
      frequency: "daily",
      nextRun: "2025-10-18 07:30",
      recipients: ["review@company.com"],
      enabled: false,
      lastRun: "2025-10-16 07:30",
      status: "paused"
    }
  ];

  const reportTypeOptions = [
    { value: 'best_price', label: 'Best Price Report' },
    { value: 'all_groups', label: 'All Product Groups' },
    { value: 'ambiguous', label: 'Ambiguous Matches' },
    { value: 'unmatched', label: 'Unmatched Products' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const handleCreateSchedule = () => {
    setIsCreatingSchedule(true);
    // Simulate API call
    setTimeout(() => {
      setIsCreatingSchedule(false);
      setScheduleForm({
        name: '',
        reportType: 'best_price',
        frequency: 'weekly',
        time: '09:00',
        recipients: '',
        enabled: true
      });
    }, 2000);
  };

  const toggleScheduleStatus = (scheduleId) => {
    console.log('Toggle schedule:', scheduleId);
  };

  const deleteSchedule = (scheduleId) => {
    console.log('Delete schedule:', scheduleId);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return 'CheckCircle';
      case 'error': return 'AlertCircle';
      case 'paused': return 'Pause';
      default: return 'Clock';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-success';
      case 'error': return 'text-error';
      case 'paused': return 'text-muted-foreground';
      default: return 'text-warning';
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Scheduled Exports</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Automate report generation and delivery
            </p>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setIsCreatingSchedule(!isCreatingSchedule)}
          >
            New Schedule
          </Button>
        </div>
      </div>
      {/* Create Schedule Form */}
      {isCreatingSchedule && (
        <div className="p-6 border-b border-border bg-muted bg-opacity-50">
          <h3 className="text-lg font-medium text-foreground mb-4">Create New Schedule</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Schedule Name"
              placeholder="e.g., Weekly Best Price Report"
              value={scheduleForm?.name}
              onChange={(e) => setScheduleForm(prev => ({ ...prev, name: e?.target?.value }))}
            />
            <Select
              label="Report Type"
              options={reportTypeOptions}
              value={scheduleForm?.reportType}
              onChange={(value) => setScheduleForm(prev => ({ ...prev, reportType: value }))}
            />
            <Select
              label="Frequency"
              options={frequencyOptions}
              value={scheduleForm?.frequency}
              onChange={(value) => setScheduleForm(prev => ({ ...prev, frequency: value }))}
            />
            <Input
              label="Time"
              type="time"
              value={scheduleForm?.time}
              onChange={(e) => setScheduleForm(prev => ({ ...prev, time: e?.target?.value }))}
            />
          </div>
          <Input
            label="Email Recipients"
            placeholder="email1@company.com, email2@company.com"
            description="Separate multiple emails with commas"
            value={scheduleForm?.recipients}
            onChange={(e) => setScheduleForm(prev => ({ ...prev, recipients: e?.target?.value }))}
            className="mb-4"
          />
          <Checkbox
            label="Enable schedule immediately"
            checked={scheduleForm?.enabled}
            onChange={(e) => setScheduleForm(prev => ({ ...prev, enabled: e?.target?.checked }))}
            className="mb-4"
          />
          <div className="flex space-x-3">
            <Button
              variant="default"
              onClick={handleCreateSchedule}
              loading={isCreatingSchedule}
              iconName="Save"
              iconPosition="left"
            >
              Create Schedule
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsCreatingSchedule(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Scheduled Exports List */}
      <div className="p-6">
        {scheduledExports?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Scheduled Exports</h3>
            <p className="text-muted-foreground mb-4">
              Create your first scheduled export to automate report delivery
            </p>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setIsCreatingSchedule(true)}
            >
              Create Schedule
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {scheduledExports?.map((schedule) => (
              <div key={schedule?.id} className="border border-border rounded-lg p-4 hover:bg-muted hover:bg-opacity-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-medium text-foreground">{schedule?.name}</h4>
                      <span className="text-xs bg-primary bg-opacity-10 text-primary px-2 py-1 rounded">
                        {schedule?.frequency}
                      </span>
                      <div className="flex items-center space-x-1">
                        <Icon 
                          name={getStatusIcon(schedule?.status)} 
                          size={14} 
                          className={getStatusColor(schedule?.status)}
                        />
                        <span className={`text-xs ${getStatusColor(schedule?.status)}`}>
                          {schedule?.enabled ? 'Active' : 'Paused'}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                      <div>
                        <span className="font-medium">Next Run:</span> {schedule?.nextRun}
                      </div>
                      <div>
                        <span className="font-medium">Last Run:</span> {schedule?.lastRun}
                      </div>
                      <div>
                        <span className="font-medium">Recipients:</span> {schedule?.recipients?.length} email{schedule?.recipients?.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleScheduleStatus(schedule?.id)}
                      iconName={schedule?.enabled ? "Pause" : "Play"}
                      title={schedule?.enabled ? "Pause schedule" : "Resume schedule"}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="Settings"
                      title="Edit schedule"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteSchedule(schedule?.id)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                      title="Delete schedule"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledExportsPanel;